import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';

import paths from '@src/paths';
import { toast } from '@src/components/core/toaster';
import defaultClient from '@src/api/clients/default';
import { useAuth } from '@src/context';

const LD_LABELS = { not_configured: 'Not set up', awaiting_test: 'Awaiting test', awaiting_confirm: 'Awaiting confirm', confirmed: 'Confirmed' };
const LD_COLORS = { not_configured: 'default', awaiting_test: 'warning', awaiting_confirm: 'info', confirmed: 'success' };

const HEALTH_RANK = { stalled: 2, at_risk: 1, on_track: 0 };
const HEALTH_LABELS = { stalled: 'Stalled', at_risk: 'At risk' };
const HEALTH_COLORS = { stalled: 'error', at_risk: 'warning' };

// What the client still owes us, in plain words.
const WAITING_LABELS = {
  Onboarding: 'Onboarding form',
  Assets: 'Logo & photos',
  'Lead Delivery': 'Lead delivery setup',
  'Lead Test': 'Lead test',
  'Lead Confirm': 'Confirm test lead',
  'Creative Review': 'Approve creative',
  'LP Approval': 'Approve landing page',
};

const CONTRACT_LABELS = {
  none: 'No contract sent',
  draft: 'Contract drafted, not sent',
  sent: 'Sent, not opened',
  viewed: 'Opened, not signed',
};

const SECTIONS = [
  {
    key: 'onboarding',
    title: 'Onboarding',
    blurb: 'Contract signed — build and launch.',
  },
  {
    key: 'contract',
    title: 'Contract pending',
    blurb: 'Not signed yet. No lanes, no build work until they sign.',
  },
  {
    key: 'vertical',
    title: 'New vertical launches',
    blurb: 'Live clients adding a vertical. Drops off once the vertical delivers its first lead.',
  },
];

const shortDate = (iso) => (iso ? new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

function sectionOf(c) {
  if (c.vertical_only) return 'vertical';
  if (c.contract_pending) return 'contract';
  return 'onboarding';
}

function LinkButtons({ c }) {
  return (
    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
      <Button component={Link} to={paths.clientHub(c.client_slug)}
        size="small" sx={{ fontSize: 11, minWidth: 0, px: 0.75 }}>Hub</Button>
      <Button component={Link} to={paths.onboarding.internal(c.client_slug)}
        size="small" sx={{ fontSize: 11, minWidth: 0, px: 0.75 }}>Checklist</Button>
      <Tooltip title="What the client sees">
        <Button component="a" href={`/onboarding/${c.client_slug}`} target="_blank"
          size="small" color="inherit" sx={{ fontSize: 11, minWidth: 0, px: 0.75, color: 'text.secondary' }}>Client view</Button>
      </Tooltip>
    </Stack>
  );
}

function ClientNameCell({ c, indented, chips }) {
  return (
    <TableCell sx={indented ? { pl: 5 } : undefined}>
      <Typography
        component={Link} to={paths.clientHub(c.client_slug)} onClick={(e) => e.stopPropagation()}
        variant="body2" fontWeight={600} noWrap
        sx={{ fontSize: 13, color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
      >
        {c.client_name}
      </Typography>
      {chips && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>{chips}</Stack>
      )}
      {(c.verticals || []).length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, display: 'block', mt: 0.25 }}>
          {c.verticals.map((v) => (v.lane ? `${v.name} — ${v.lane}` : v.name)).join(' · ')}
        </Typography>
      )}
    </TableCell>
  );
}

function OwnerCell({ c }) {
  return (
    <TableCell>
      <Typography variant="caption" sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
        color={c.owner ? 'text.primary' : 'text.disabled'} title={c.owner?.email || 'Unassigned'}>
        {c.owner ? c.owner.name.split(' ')[0] : 'Unassigned'}
      </Typography>
    </TableCell>
  );
}

// ---- Onboarding (signed) --------------------------------------------------
const ONBOARDING_COLUMNS = ['Client', 'Age', 'Progress', 'Our next step', 'Waiting on client', 'Lead delivery', 'Owner', ''];

function OnboardingRow({ c, navigate, indented }) {
  const waiting = c.waiting_on_client || [];
  return (
    <TableRow hover onClick={() => navigate(paths.onboarding.internal(c.client_slug))}
      sx={{ cursor: 'pointer', '& td': { py: 1, px: 1.5, fontSize: 13, verticalAlign: 'top' } }}>
      <ClientNameCell c={c} indented={indented} chips={(HEALTH_LABELS[c.health] || ['none', 'parent'].includes(c.contract?.state)) && (
        <>
          {HEALTH_LABELS[c.health] && (
            <Chip
              label={`${HEALTH_LABELS[c.health]}${c.stalled_on ? ` — on ${c.stalled_on === 'guru' ? 'us' : 'client'}` : ''}`}
              size="small" color={HEALTH_COLORS[c.health]} sx={{ fontSize: 10, height: 18, fontWeight: 700 }}
            />
          )}
          {c.contract?.state === 'none' && (
            <Chip label="No contract on file" size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
          )}
          {c.contract?.state === 'parent' && (
            <Tooltip title={`Covered by ${c.parent_company?.name || 'the parent company'}'s existing agreement — no contract of its own needed`}>
              <Chip label="Parent agreement" size="small" color="info" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
            </Tooltip>
          )}
        </>
      )} />
      <TableCell>
        {c.days_in_onboarding != null ? (
          <>
            <Typography variant="body2" fontWeight={700} sx={{
              fontSize: 13,
              color: c.health === 'stalled' ? 'error.main' : c.health === 'at_risk' ? 'warning.main' : 'text.primary',
            }}>
              {c.days_in_onboarding}d
            </Typography>
            {c.idle_days != null && c.idle_days >= 3 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block' }}>
                no activity {c.idle_days}d
              </Typography>
            )}
          </>
        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
      </TableCell>
      <TableCell sx={{ minWidth: 110 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <LinearProgress variant="determinate" value={c.progress || 0}
            sx={{ flex: 1, height: 5, borderRadius: 3 }}
            color={c.progress === 100 ? 'success' : 'primary'} />
          <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>{c.completed_tasks}/{c.total_tasks}</Typography>
        </Stack>
      </TableCell>
      <TableCell>
        {c.next_step ? (
          <>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12.5 }}>{c.next_step.title}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
              {c.next_step.phase}{c.estimated_launch ? ` · est. launch ${shortDate(c.estimated_launch)}` : ''}
            </Typography>
          </>
        ) : (
          <Chip label="Our side done" size="small" color="success" sx={{ fontSize: 10, height: 20 }} />
        )}
      </TableCell>
      <TableCell>
        {waiting.length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {waiting.map((w) => (
              <Chip key={w} label={WAITING_LABELS[w] || w} size="small" color="warning" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
            ))}
          </Stack>
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
            <Typography variant="caption" color="text.secondary">Nothing</Typography>
          </Stack>
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={LD_LABELS[c.lead_delivery_status] || '—'}
          size="small"
          color={LD_COLORS[c.lead_delivery_status] || 'default'}
          variant={c.lead_delivery_status === 'confirmed' ? 'filled' : 'outlined'}
          sx={{ fontSize: 10, height: 18 }}
        />
      </TableCell>
      <OwnerCell c={c} />
      <TableCell align="right" onClick={(e) => e.stopPropagation()}><LinkButtons c={c} /></TableCell>
    </TableRow>
  );
}

// ---- Contract pending ------------------------------------------------------
const CONTRACT_COLUMNS = ['Client', 'Contract', 'Sent', 'Reminders', 'Owner', ''];

function ContractRow({ c, navigate, indented }) {
  const k = c.contract || {};
  const days = k.days;
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  // Re-sends the original signing email (the automatic reminders keep
  // running on their own schedule).
  const handleResend = async () => {
    if (!window.confirm(`Re-send the contract signing link to ${k.signer_email}?`)) return;
    setResending(true);
    try {
      await defaultClient.request({ url: `contracts/${k.id}/send/`, method: 'POST' });
      setResent(true);
      toast.success(`Contract re-sent to ${k.signer_email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to re-send contract');
    } finally {
      setResending(false);
    }
  };
  return (
    <TableRow hover onClick={() => navigate(paths.clientHub(c.client_slug))}
      sx={{ cursor: 'pointer', '& td': { py: 1, px: 1.5, fontSize: 13, verticalAlign: 'top' } }}>
      <ClientNameCell c={c} indented={indented} />
      <TableCell>
        <Chip
          label={CONTRACT_LABELS[k.state] || k.state}
          size="small"
          color={k.state === 'viewed' ? 'info' : k.state === 'sent' ? 'warning' : 'default'}
          variant="outlined"
          sx={{ fontSize: 10.5, height: 20, fontWeight: 600 }}
        />
      </TableCell>
      <TableCell>
        {k.date ? (
          <>
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: 13, color: days >= 7 ? 'warning.main' : 'text.primary' }}>
              {days != null ? `${days}d ago` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{shortDate(k.date)}</Typography>
          </>
        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
      </TableCell>
      <TableCell>
        <Typography variant="caption" sx={{ fontSize: 11 }} color={k.reminders ? 'text.primary' : 'text.disabled'}>
          {k.reminders ? `${k.reminders} sent` : '—'}
        </Typography>
      </TableCell>
      <OwnerCell c={c} />
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
          {k.resendable && (
            <Tooltip title={`Email the signing link to ${k.signer_email} again`}>
              <span>
                <Button size="small" variant="outlined" disabled={resending || resent} onClick={handleResend}
                  sx={{ fontSize: 11, minWidth: 0, px: 1, whiteSpace: 'nowrap' }}>
                  {resent ? 'Re-sent' : resending ? 'Sending…' : 'Re-send contract'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button component={Link} to={paths.clientHub(c.client_slug)}
            size="small" sx={{ fontSize: 11, minWidth: 0, px: 0.75 }}>Hub</Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

// ---- New vertical launches -------------------------------------------------
const VERTICAL_COLUMNS = ['Client', 'Vertical', 'Open for', 'Progress', ''];

function VerticalRows({ c, navigate, indented }) {
  return (c.vertical_flows || []).map((vf) => (
    <TableRow key={`${c.client_id}-vf-${vf.vertical_id}`} hover
      onClick={() => navigate(`${paths.onboarding.internal(c.client_slug)}?vertical=${vf.vertical_id}`)}
      sx={{ cursor: 'pointer', '& td': { py: 1, px: 1.5, fontSize: 13, verticalAlign: 'top' } }}>
      <ClientNameCell c={{ ...c, verticals: [] }} indented={indented} />
      <TableCell>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>{vf.vertical_name}</Typography>
          {vf.lane && <Chip label={vf.lane} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />}
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={700} sx={{ fontSize: 13, color: vf.days_in >= 21 ? 'warning.main' : 'text.primary' }}>
          {vf.days_in != null ? `${vf.days_in}d` : '—'}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 110 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <LinearProgress variant="determinate" value={vf.progress} color="secondary"
            sx={{ flex: 1, height: 5, borderRadius: 3 }} />
          <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>{vf.complete}/{vf.total}</Typography>
        </Stack>
      </TableCell>
      <TableCell align="right" onClick={(e) => e.stopPropagation()}><LinkButtons c={c} /></TableCell>
    </TableRow>
  ));
}

const SECTION_CONFIG = {
  onboarding: { columns: ONBOARDING_COLUMNS, Row: OnboardingRow },
  contract: { columns: CONTRACT_COLUMNS, Row: ContractRow },
  vertical: { columns: VERTICAL_COLUMNS, Row: VerticalRows },
};

// Parent companies become a collapsible header over their clients. Groups
// sort worst health first, then oldest — "who needs a push?" on top.
function groupRows(list) {
  const severity = (c) => HEALTH_RANK[c.health] ?? 0;
  const age = (c) => c.days_in_onboarding ?? c.contract?.days ?? -1;
  const parents = new Map();
  const groups = [];
  list.forEach((c) => {
    if (c.parent_company) {
      if (!parents.has(c.parent_company.id)) {
        const g = { type: 'parent', parent: c.parent_company, name: c.parent_company.name || '', children: [] };
        parents.set(c.parent_company.id, g);
        groups.push(g);
      }
      parents.get(c.parent_company.id).children.push(c);
    } else {
      groups.push({ type: 'standalone', name: c.client_name || '', client: c });
    }
  });
  groups.forEach((g) => {
    const members = g.children || [g.client];
    g.severity = Math.max(...members.map(severity));
    g.age = Math.max(...members.map(age));
    if (g.children) g.children.sort((a, b) => severity(b) - severity(a) || age(b) - age(a));
  });
  groups.sort((a, b) => b.severity - a.severity || b.age - a.age || a.name.localeCompare(b.name));
  return groups;
}

function Section({ sectionKey, title, blurb, clients, navigate, collapsedParents, toggleParent }) {
  const { columns, Row } = SECTION_CONFIG[sectionKey];
  const groups = useMemo(() => groupRows(clients), [clients]);
  return (
    <Card variant="outlined" sx={{ mb: 2.5 }}>
      <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
          <Chip label={clients.length} size="small" sx={{ fontSize: 11, height: 20, fontWeight: 700 }} />
        </Stack>
        <Typography variant="caption" color="text.secondary">{blurb}</Typography>
      </Box>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, py: 1, px: 1.5, whiteSpace: 'nowrap' } }}>
              {columns.map((col, i) => <TableCell key={i}>{col}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => {
              if (group.type === 'standalone') {
                return <Row key={group.client.client_id} c={group.client} navigate={navigate} indented={false} />;
              }
              const { parent, children } = group;
              const collapsed = !!collapsedParents[`${sectionKey}-${parent.id}`];
              return (
                <React.Fragment key={`parent-${parent.id}`}>
                  <TableRow hover onClick={() => toggleParent(`${sectionKey}-${parent.id}`)}
                    sx={{ cursor: 'pointer', bgcolor: 'grey.50', '& td': { py: 0.75, px: 1.5, fontSize: 13 } }}>
                    <TableCell colSpan={columns.length}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          {collapsed ? <KeyboardArrowRightIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                        <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: 13 }}>{parent.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          · {children.length} client{children.length !== 1 ? 's' : ''}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {!collapsed && children.map((c) => <Row key={c.client_id} c={c} navigate={navigate} indented />)}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function OnboardingDashboardPage() {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const currentEmail = authState?.user?.email || '';
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [collapsedParents, setCollapsedParents] = useState({});

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await defaultClient.request({ url: 'tasks/onboarding-tasks/dashboard/' });
      setClients(res?.data?.results || []);
      setSummary(res?.data?.summary || null);
    } catch {
      toast.error('Failed to load onboarding dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const bySection = useMemo(() => {
    let list = clients;
    if (ownerFilter === 'mine') list = list.filter((c) => c.owner?.email === currentEmail);
    if (ownerFilter === 'unassigned') list = list.filter((c) => !c.owner);
    const out = { onboarding: [], contract: [], vertical: [] };
    list.forEach((c) => {
      const key = sectionOf(c);
      if (key !== 'vertical') out[key].push(c);
      if ((c.vertical_flows || []).length) out.vertical.push(c);
    });
    return out;
  }, [clients, ownerFilter, currentEmail]);

  const toggleParent = (key) => setCollapsedParents((prev) => ({ ...prev, [key]: !prev[key] }));

  const visibleSections = SECTIONS.filter((s) => (!showFilter || showFilter === s.key) && bySection[s.key].length > 0);

  const kpis = summary ? [
    { label: 'Contract pending', value: summary.contract_pending ?? bySection.contract.length, color: 'text.primary', hint: 'Sent, not signed' },
    { label: 'Stalled', value: summary.stalled, color: summary.stalled > 0 ? 'error.main' : 'text.primary', hint: 'Signed clients' },
    { label: 'At risk', value: summary.at_risk, color: summary.at_risk > 0 ? 'warning.main' : 'text.primary', hint: 'Signed clients' },
    { label: 'Waiting on client 3d+', value: summary.waiting_on_client, color: 'text.primary' },
    {
      label: 'Median days to launch',
      value: summary.median_days_to_launch_90d != null ? `${summary.median_days_to_launch_90d}d` : '—',
      hint: summary.launches_90d ? `last 90d · ${summary.launches_90d} launch${summary.launches_90d !== 1 ? 'es' : ''}` : 'no launches yet',
      color: 'text.primary',
    },
  ] : [];

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap spacing={1.5}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Onboarding</Typography>
          <Typography variant="body2" color="text.secondary">
            Who needs a push, and whose move it is. Click a row for the checklist, or a name for the Client Hub.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <TextField select size="small" label="Owner" value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="">Everyone</MenuItem>
            <MenuItem value="mine">Mine</MenuItem>
            <MenuItem value="unassigned">Unassigned</MenuItem>
          </TextField>
          <TextField select size="small" label="Show" value={showFilter}
            onChange={(e) => setShowFilter(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">Everything</MenuItem>
            {SECTIONS.map((s) => <MenuItem key={s.key} value={s.key}>{s.title}</MenuItem>)}
          </TextField>
          <Button startIcon={<RefreshIcon />} onClick={loadData} size="small">Refresh</Button>
        </Stack>
      </Stack>

      {kpis.length > 0 && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
          {kpis.map((kpi) => (
            <Card key={kpi.label} variant="outlined" sx={{ px: 2, py: 1, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {kpi.label}
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ color: kpi.color, lineHeight: 1.2 }}>{kpi.value}</Typography>
              {kpi.hint && <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>{kpi.hint}</Typography>}
            </Card>
          ))}
        </Stack>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={32} /></Box>
      ) : visibleSections.length === 0 ? (
        <Card variant="outlined">
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">
              {ownerFilter || showFilter ? 'Nothing matches these filters.' : 'No clients in onboarding.'}
            </Typography>
          </Box>
        </Card>
      ) : (
        visibleSections.map((s) => (
          <Section key={s.key} sectionKey={s.key} title={s.title} blurb={s.blurb}
            clients={bySection[s.key]} navigate={navigate}
            collapsedParents={collapsedParents} toggleParent={toggleParent} />
        ))
      )}
    </Box>
  );
}
